import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import type { AnswerWidgets } from '@openx/components-core/lib/AiChat/answerWidgets';
import { getMessageId } from '@openx/components-core/lib/AiChat/messageId';
import type { Message } from '@openx/components-core/lib/AiChat/types';
import { answerWidgetClipboardComponents } from '@openx/components-core/lib/Markdown/widgets/answerWidgetClipboard';
import type { MessageActionState } from '@openx/components-deal/lib/DealDetails/constants';
import { Copy, ThumbsDown, ThumbsUp } from '@phosphor-icons/react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createOpenXAgentAnswerWidgetClipboardRenderer } from '../../../AnswerWidgets/openXAgentAnswerWidgets';
import { getWidgetNotices } from '../../../AnswerWidgets/utils/widgetNotices';
import { copyTroubleshootingMessage } from '../../utils/copyTroubleshootingMessage';
import { ChatActionButton } from '../ChatActionButton/ChatActionButton';
import { FeedbackModal } from '../FeedbackModal/FeedbackModal';

export type TroubleshootingMessage = Message & { langfuse_trace_id?: string };

type FeedbackMessageActionsProps = {
  message: TroubleshootingMessage;
  widgets?: AnswerWidgets | undefined;
  msgState: MessageActionState | undefined;
  isSubmitting: boolean;
  onThumbUp: (traceId: string, messageId: string) => void;
  onThumbDown: (messageId: string) => void;
  onSubmitFeedback: (messageId: string, traceId: string, feedbackText: string) => Promise<void>;
  onCancelFeedback: (messageId: string) => void;
  disabled?: boolean;
};

export const FeedbackMessageActions = ({
  message,
  widgets,
  msgState,
  isSubmitting,
  onThumbUp,
  onThumbDown,
  onSubmitFeedback,
  onCancelFeedback,
  disabled = false,
}: FeedbackMessageActionsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const traceId = message.langfuse_trace_id;
  const messageId = getMessageId(message);

  // when copying widgets are turned to readable text
  const copyMessage = useCallback(
    () =>
      copyTroubleshootingMessage(
        message,
        answerWidgetClipboardComponents(widgets, {
          ...getWidgetNotices(t),
          renderWidget: createOpenXAgentAnswerWidgetClipboardRenderer(t),
        })
      ),
    [message, widgets, t]
  );

  if (message.role === 'event') {
    return null;
  }

  if (!traceId) {
    return (
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1,
        }}
      >
        <ChatActionButton
          label={t('Copy to clipboard')}
          clickedLabel={t('Copied!')}
          onClick={copyMessage}
          Icon={Copy}
          disabled={disabled}
        />
      </Stack>
    );
  }

  return (
    <Stack
      direction="column"
      sx={{
        gap: 1,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 1,
        }}
      >
        <ChatActionButton
          label={t('Good answer')}
          clickedLabel={t('Feedback submitted')}
          onClick={() => onThumbUp(traceId, messageId)}
          Icon={ThumbsUp}
          pressedColor={theme.palette.success.main}
          disabled={disabled}
          pressed={msgState?.thumbUp}
        />
        <ChatActionButton
          label={t('Bad answer')}
          onClick={() => onThumbDown(messageId)}
          Icon={ThumbsDown}
          pressedColor={theme.palette.error.main}
          disabled={disabled}
          pressed={msgState?.thumbDown}
        />
        <ChatActionButton
          label={t('Copy to clipboard')}
          clickedLabel={t('Copied!')}
          onClick={copyMessage}
          Icon={Copy}
          disabled={disabled}
        />
      </Stack>

      {msgState?.requestFeedback && (
        <FeedbackModal
          onSubmit={feedbackText => onSubmitFeedback(messageId, traceId, feedbackText)}
          onCancel={() => onCancelFeedback(messageId)}
          isSubmitting={isSubmitting}
        />
      )}
    </Stack>
  );
};
